import { createRule } from '@regle/core'
import { FLEXIBLE_CHECK_IN_OUT } from '~/constants'

import type { COUNTRY_SHORTS } from '~/constants'
import type { Maybe } from '@regle/core'

/**
 * Checks if an email address is already in use or not.
 */
export const isEmailAvailable = createRule({
  type: 'isEmailAvailable',
  async: true,
  message: '',
  validator: async (value: Maybe<string>) => {
    if (!value) return true

    const { $rentals } = useNuxtApp()

    return await $rentals('/api/account/is-email-available', { query: { email: value } })
  },
})

/**
 * Validates a phone number, this depends on vue-tel-input's validator.
 */
export const phoneValid = createRule({
  type: 'phoneValid',
  message: '',
  validator: async (value: Maybe<string>, callingCode: Maybe<string>) => {
    if (!value) {
      return true
    }

    const code = (callingCode ?? '').replace('+', '')

    if (!code) {
      return false
    }

    const { parsePhoneNumberFromString } = await import('libphonenumber-js')

    return parsePhoneNumberFromString(`+${code}${value}`)?.isValid() === true
  },
})

/**
 * Checks that a value differs from another (reactive) value. Passes when either
 * is empty.
 */
export const phoneDifferent = createRule({
  type: 'phoneDifferent',
  message: '',
  validator: (value: Maybe<string>, primary: Maybe<string>) => {
    const left = (value ?? '').replace(/\D/g, '')
    const right = (primary ?? '').replace(/\D/g, '')
    return !left || !right || left !== right
  },
})

/**
 * Checks if a password passes the upper-/lowercasing criterium.
 */
export const passwordCasing = createRule({
  type: 'passwordCasing',
  message: '',
  validator: (value: Maybe<string>) => passwordCasingCriteriaValidator(value ?? ''),
})

/**
 * Checks if a password passes the digits/symbols criterium.
 */
export const passwordNumberOrSymbol = createRule({
  type: 'passwordNumberOrSymbol',
  message: '',
  validator: (value: Maybe<string>) => passwordNumberOrSymbolCriteriaValidator(value ?? ''),
})

/**
 * Checks a postal/ZIP code against the selected country's format.
 */
export const postalCode = createRule({
  type: 'postalCode',
  message: '',
  validator: (value: Maybe<string>, countryCode: Maybe<string>) =>
    postalCodeValidatorByCountryCode(value ?? '', countryCode ?? ''),
})

/**
 * Checks an address.
 */
export const address = createRule({
  type: 'address',
  message: '',
  validator: (value: Maybe<string>) => addressValidator(value ?? ''),
})

/**
 * Start time must be earlier than the end time. A flexible end time always
 * passes.
 */
export const timeBefore = createRule({
  type: 'timeBefore',
  message: '',
  validator: (value: Maybe<number>, end: Maybe<number>) =>
    value == null || end == null || end === FLEXIBLE_CHECK_IN_OUT || value < end,
})

/**
 * End time must be later than the start time. A flexible end time always
 * passes.
 */
export const timeAfter = createRule({
  type: 'timeAfter',
  message: '',
  validator: (value: Maybe<number>, start: Maybe<number>) =>
    value == null || start == null || value === FLEXIBLE_CHECK_IN_OUT || value > start,
})

/**
 * Checks if a URL is a Matterport one.
 */
export const matterportUrl = createRule({
  type: 'matterportUrl',
  message: '',
  validator: (value: Maybe<string>) => matterportUrlValidator(value ?? ''),
})

/**
 * Checks if a driver's license is valid.
 */
export const driverLicense = createRule({
  type: 'driverLicense',
  message: '',
  validator: (value: Maybe<string>, countryCode: COUNTRY_SHORTS, regionCode: string) =>
    !value || driverLicenseValidatorByCountryCode(value, countryCode, regionCode),
})

/**
 * Checks that the typed address is the one picked from the suggestions, and
 * that the pick resolved to coordinates. Passes when empty.
 */
export const addressSelected = createRule({
  type: 'addressSelected',
  message: '',
  validator: (value: Maybe<string>, selected: Maybe<string>, hasCoordinates: boolean) =>
    !value || (value === selected && hasCoordinates === true),
})

/**
 * Checks if a collection name is unique.
 */
export const collectionNameIsUnique = createRule({
  type: 'collectionNameIsUnique',
  message: '',
  validator: (value: Maybe<string>, names: string[], currentName: Maybe<string>) => {
    const normalized = (value ?? '').toLowerCase()
    if (!normalized) {
      return true
    }
    const taken = names.some((name) => name.toLowerCase() === normalized)
    return !taken || currentName?.toLowerCase() === normalized
  },
})

/**
 * Checks if a value is the same as another value, case-insensitive.
 */
export const sameAsCaseInsensitive = createRule({
  type: 'sameAsCaseInsensitive',
  message: '',
  validator: (value: Maybe<string>, expected: Maybe<string>) =>
    !value || caseInsensitiveMatchValidator(expected ?? '')(value),
})
